import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, API, fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useNavigate, useParams } from "react-router-dom";
import {
  Save, Sparkles, Plus, X, QrCode, Loader2, CheckCircle2, XCircle, ExternalLink, Wand2, Trash2, RotateCw, Undo2,
} from "lucide-react";
import QrModal from "@/components/QrModal";
import SortableImageGrid from "@/components/SortableImageGrid";
import {
  emptySuggestions,
  Section,
  Field,
  ListEditor,
  ReextractButton,
  SuggestionGroup,
  SuggestionFaqGroup,
  SourceFilesEditor,
} from "@/components/product-edit/parts";

const FIELDS_TEXT = [
  ["name", "Product Name"],
  ["code", "Product Code / SKU"],
  ["category", "Category"],
  ["dimensions", "Dimensions"],
  ["thickness", "Thickness"],
  ["material", "Material"],
];

const PRICE_UNIT_OPTIONS = [
  { value: "piece", label: "Per piece" },
  { value: "box", label: "Per box" },
  { value: "sqft", label: "Per square foot" },
  { value: "sheet", label: "Per sheet" },
  { value: "running_ft", label: "Per running foot" },
  { value: "custom", label: "Custom unit…" },
];

const LISTS = [
  ["available_sizes", "Available Sizes"],
  ["available_colors", "Available Colors"],
  ["applications", "Applications"],
];

export default function AdminProductEdit() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [generatingScenes, setGeneratingScenes] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();

  const [reloadTick, setReloadTick] = useState(0);

  const load = async () => {
    const { data } = await api.get(`/products/${id}`);
    setP({
      ...data,
      approved_suggestions: data.approved_suggestions || emptySuggestions(),
    });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await api.get(`/products/${id}`);
      if (!cancelled) {
        setP({
          ...data,
          approved_suggestions: data.approved_suggestions || emptySuggestions(),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, reloadTick]);

  // Auto-poll while extraction is still running so the page updates as data comes in.
  useEffect(() => {
    if (!p) return;
    if (p.extraction_status !== "processing" && p.extraction_status !== "pending") return;
    const handle = setInterval(() => setReloadTick((t) => t + 1), 5000);
    return () => clearInterval(handle);
  }, [p?.extraction_status]); // eslint-disable-line react-hooks/exhaustive-deps

  const upd = (key, val) => setP((x) => ({ ...x, [key]: val }));
  const updList = (key, idx, val) =>
    setP((x) => ({ ...x, [key]: x[key].map((v, i) => (i === idx ? val : v)) }));
  const addList = (key) => setP((x) => ({ ...x, [key]: [...(x[key] || []), ""] }));
  const remList = (key, idx) =>
    setP((x) => ({ ...x, [key]: x[key].filter((_, i) => i !== idx) }));

  const updSpec = (idx, field, val) =>
    setP((x) => ({
      ...x,
      specifications: x.specifications.map((s, i) =>
        i === idx ? { ...s, [field]: val } : s,
      ),
    }));
  const addSpec = () =>
    setP((x) => ({ ...x, specifications: [...(x.specifications || []), { key: "", value: "" }] }));
  const remSpec = (idx) =>
    setP((x) => ({ ...x, specifications: x.specifications.filter((_, i) => i !== idx) }));

  // suggestion approvals: copy items into approved_suggestions
  const isApproved = (group, value) =>
    (p?.approved_suggestions?.[group] || []).some((v) =>
      typeof v === "string" ? v === value : JSON.stringify(v) === JSON.stringify(value),
    );

  const toggleApprove = (group, value) => {
    setP((x) => {
      const list = x.approved_suggestions[group] || [];
      const exists = list.some((v) =>
        typeof v === "string" ? v === value : JSON.stringify(v) === JSON.stringify(value),
      );
      const next = exists
        ? list.filter((v) =>
            typeof v === "string" ? v !== value : JSON.stringify(v) !== JSON.stringify(value),
          )
        : [...list, value];
      return { ...x, approved_suggestions: { ...x.approved_suggestions, [group]: next } };
    });
  };

  const save = async (extraPatch = {}) => {
    setSaving(true);
    try {
      const payload = {
        name: p.name,
        code: p.code,
        category: p.category,
        description: p.description,
        features: (p.features || []).filter(Boolean),
        specifications: (p.specifications || []).filter((s) => s.key || s.value),
        variants: (p.variants || [])
          .filter((v) => v && v.code)
          .map((v) => ({
            code: v.code,
            finish: v.finish || null,
            length: v.length || null,
            coverage: v.coverage || null,
            weight: v.weight || null,
            thickness: v.thickness || null,
            notes: v.notes || null,
          })),
        dimensions: p.dimensions,
        thickness: p.thickness,
        material: p.material,
        available_sizes: (p.available_sizes || []).filter(Boolean),
        available_colors: (p.available_colors || []).filter(Boolean),
        applications: (p.applications || []).filter(Boolean),
        technical_info: p.technical_info,
        installation_instructions: p.installation_instructions,
        approved_suggestions: p.approved_suggestions,
        prices: (p.prices || [])
          .filter((pr) => pr.unit && pr.value !== "" && pr.value != null)
          .map((pr) => ({
            unit: pr.unit,
            label: pr.label || "",
            value: parseFloat(pr.value) || 0,
            currency: pr.currency || "INR",
            notes: pr.notes || "",
          })),
        ...extraPatch,
      };
      const { data } = await api.patch(`/products/${id}`, payload);
      setP({ ...data, approved_suggestions: data.approved_suggestions || emptySuggestions() });
      return data;
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    if (!p.name || !p.code) {
      alert("Please set Product Name and Product Code before publishing.");
      return;
    }
    setPublishing(true);
    try {
      await save();
      const { data } = await api.post(`/products/${id}/publish`);
      setP({ ...data, approved_suggestions: data.approved_suggestions || emptySuggestions() });
    } catch (e) {
      alert(e?.response?.data?.detail || "Publish failed");
    } finally {
      setPublishing(false);
    }
  };

  const reExtract = async () => {
    setExtracting(true);
    try {
      await api.post(`/products/${id}/extract`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Extraction failed");
    } finally {
      setExtracting(false);
    }
  };

  const uploadMoreImages = async (files) => {
    const fd = new FormData();
    const token = localStorage.getItem("heda_token");
    for (const f of files) {
      fd.append("file", f);
      fd.append("kind", "image");
      const res = await fetch(`${API}/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      setP((x) => ({ ...x, images: [...(x.images || []), data] }));
      fd.delete("file");
      fd.delete("kind");
    }
    await api.patch(`/products/${id}`, {});
  };

  const removeImage = async (fileId) => {
    if (!window.confirm("Remove this image from the product?")) return;
    try {
      await api.delete(`/products/${id}/images/${fileId}`);
      setP((x) => ({
        ...x,
        images: (x.images || []).filter((i) => i.id !== fileId),
      }));
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to remove image");
    }
  };

  const generateScenes = async () => {
    if (!p.name) {
      alert("Please save a product name first — it's used to generate the scenes.");
      return;
    }
    setGeneratingScenes(true);
    try {
      const { data } = await api.post(`/products/${id}/generate-scenes?count=4`);
      if (data?.status === "generating") {
        // Async — start polling for completion. The backend appends images and flips
        // scene_status back to "idle" (or "failed" with a reason) when done.
        setP((x) => ({ ...x, scene_status: "generating", scene_error: null }));
      } else if (data?.images) {
        // Legacy synchronous response — pre-async backend.
        setP((x) => ({ ...x, images: data.images }));
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Scene generation failed";
      alert(detail);
      setGeneratingScenes(false);
    }
  };

  // Poll product while scene generation is in progress. Stops when scene_status
  // is no longer "generating".
  useEffect(() => {
    if (p?.scene_status !== "generating") return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/products/${id}`);
        if (cancelled) return;
        setP((x) => ({
          ...data,
          approved_suggestions:
            data.approved_suggestions || x?.approved_suggestions || emptySuggestions(),
        }));
        if (data.scene_status !== "generating") {
          setGeneratingScenes(false);
          if (data.scene_status === "failed" && data.scene_error) {
            alert(data.scene_error);
          }
        }
      } catch {
        // ignore transient errors; keep polling
      }
    };
    const handle = setInterval(tick, 5000);
    // Also poll once immediately
    tick();
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p?.scene_status, id]);

  // Poll product while enrichment (page snapshots + Perplexity research) is running.
  // Stops as soon as enrichment_status flips to "done" or "failed". This way the
  // research notes and reference sheets appear automatically without a manual refresh.
  useEffect(() => {
    if (p?.enrichment_status !== "running") return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/products/${id}`);
        if (cancelled) return;
        setP((x) => ({
          ...data,
          approved_suggestions:
            data.approved_suggestions || x?.approved_suggestions || emptySuggestions(),
        }));
      } catch {
        // ignore transient errors; keep polling
      }
    };
    const handle = setInterval(tick, 4000);
    tick();
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p?.enrichment_status, id]);

  const consolidateSpecs = async () => {
    if (!window.confirm(
      "Consolidate the specifications table?\n\nThis groups any 'GL 11 Length', 'GL 11 Weight'… style rows into a single Profile Variants table. " +
      "Global specs (Material Grade, Warranty, Tolerance…) stay as normal rows. " +
      "No AI is re-run, so this is free and instant.",
    )) return;
    try {
      const { data } = await api.post(`/products/${id}/consolidate-specs`);
      setP({ ...data, approved_suggestions: data.approved_suggestions || emptySuggestions() });
    } catch (e) {
      alert(e?.response?.data?.detail || "Consolidation failed");
    }
  };

  // Per-field re-extraction: AI re-reads ONLY the named field from the source PDF and
  // updates it in place. Used by the small "↻" button next to each field section.
  const [reextracting, setReextracting] = useState(null); // field name currently re-extracting
  const reextractField = async (field, friendly) => {
    if (
      !window.confirm(
        `Re-extract "${friendly}" from the original PDF?\n\nThis will REPLACE the current value with what Gemini reads fresh from the source files. Other fields are not touched. You can Undo if the new value is worse.`,
      )
    ) return;
    setReextracting(field);
    try {
      const { data } = await api.post(`/products/${id}/re-extract-field`, { field });
      setP({ ...data, approved_suggestions: data.approved_suggestions || emptySuggestions() });
    } catch (e) {
      alert(e?.response?.data?.detail || `Failed to re-extract ${field}`);
    } finally {
      setReextracting(null);
    }
  };

  const undoField = async (field, friendly) => {
    if (
      !window.confirm(
        `Undo last AI re-extraction of "${friendly}"?\n\nThe value will be restored to what it was before you clicked Re-extract.`,
      )
    ) return;
    setReextracting(field);
    try {
      const { data } = await api.post(`/products/${id}/undo-field`, { field });
      setP({ ...data, approved_suggestions: data.approved_suggestions || emptySuggestions() });
    } catch (e) {
      alert(e?.response?.data?.detail || `Failed to undo ${field}`);
    } finally {
      setReextracting(null);
    }
  };

  const deleteProduct = async () => {
    const label = p.name || p.code || "this product";
    if (
      !window.confirm(
        `Delete "${label}"? This permanently removes the product, its AI suggestions and reference sheets. This cannot be undone.`,
      )
    ) {
      return;
    }
    setDeleting(true);
    try {
      await api.delete(`/products/${id}`);
      nav("/admin/products");
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to delete product");
      setDeleting(false);
    }
  };

  if (!p) {
    return (
      <AdminLayout>
        <div className="text-sm text-[#5C5C54]">Loading...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="hed-overline">
            {p.status === "published" ? "Published Product" : "Draft Product"}
          </div>
          <h1 className="font-serif text-3xl md:text-4xl mt-2 tracking-tight">
            {p.name || "Untitled product"}
          </h1>
          <div className="flex items-center gap-3 mt-2 text-xs text-[#5C5C54] flex-wrap">
            <span>CODE · {p.code || "—"}</span>
            <span>·</span>
            <span>AI: {p.extraction_status}{p.extraction_stage ? ` (${p.extraction_stage.replace(/_/g, " ")})` : ""}</span>
            {p.enrichment_status === "running" && (
              <>
                <span>·</span>
                <span
                  className="inline-flex items-center gap-1.5 text-[#B5953B]"
                  data-testid="enrichment-pill"
                >
                  <Loader2 size={11} className="animate-spin" />
                  Enriching (page snapshots &amp; web research)
                </span>
              </>
            )}
          </div>
          {p.extraction_status === "failed" && p.extraction_error && (
            <div
              className="mt-3 bg-[#9E2A2B]/10 border border-[#9E2A2B]/30 text-[#9E2A2B] text-xs rounded-sm p-3 max-w-xl"
              data-testid="extraction-error-banner"
            >
              <div className="font-semibold mb-1">AI extraction failed</div>
              <div className="font-mono text-[11px] leading-relaxed break-words">
                {p.extraction_error}
              </div>
              <div className="text-[11px] mt-2 opacity-80">
                Click <strong>Re-run AI</strong> on the right to try again.
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {user?.role === "owner" && (
            <button
              onClick={deleteProduct}
              disabled={deleting}
              className="inline-flex items-center gap-2 border border-[#E8E6DF] text-[#9E2A2B] hover:bg-[#9E2A2B]/10 hover:border-[#9E2A2B]/40 disabled:opacity-60 px-4 py-2.5 rounded-sm text-sm transition-colors"
              data-testid="delete-product-button"
              title="Delete this product"
            >
              {deleting ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Trash2 size={14} strokeWidth={1.5} />
              )}
              Delete
            </button>
          )}
          <button
            onClick={reExtract}
            disabled={extracting}
            className="inline-flex items-center gap-2 border border-[#E8E6DF] hover:bg-[#F2F0EA] px-4 py-2.5 rounded-sm text-sm transition-colors"
            data-testid="re-extract-button"
          >
            {extracting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} strokeWidth={1.5} />}
            Re-run AI
          </button>
          <button
            onClick={() => save()}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#5E6033] hover:bg-[#4A4C28] text-white px-4 py-2.5 rounded-sm text-sm transition-colors"
            data-testid="save-product-button"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} strokeWidth={1.5} />}
            Save draft
          </button>
          {p.status === "published" ? (
            <>
              <button
                onClick={() => setQrOpen(true)}
                className="inline-flex items-center gap-2 border border-[#E8E6DF] hover:bg-[#F2F0EA] px-4 py-2.5 rounded-sm text-sm transition-colors"
                data-testid="open-qr-button"
              >
                <QrCode size={14} strokeWidth={1.5} /> QR
              </button>
              <a
                href={`/product/${p.code}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 bg-[#1A1A18] hover:bg-[#2a2a26] text-white px-4 py-2.5 rounded-sm text-sm transition-colors"
                data-testid="open-public-link"
              >
                Open page <ExternalLink size={14} strokeWidth={1.5} />
              </a>
            </>
          ) : (
            <button
              onClick={publish}
              disabled={publishing}
              className="inline-flex items-center gap-2 bg-[#B5953B] hover:bg-[#9A7E32] text-white px-4 py-2.5 rounded-sm text-sm transition-colors"
              data-testid="publish-product-button"
            >
              {publishing ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} strokeWidth={1.5} />}
              Publish
            </button>
          )}
        </div>
      </div>

      {/* Main grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: Form */}
        <div className="lg:col-span-2 space-y-6">
          <Section title="Identity">
            <div className="grid sm:grid-cols-2 gap-5">
              {FIELDS_TEXT.map(([k, label]) => (
                <Field key={k} label={label}>
                  <input
                    data-testid={`field-${k}`}
                    className="w-full bg-transparent border-b border-[#E8E6DF] focus:border-[#B5953B] focus:outline-none py-2 text-sm"
                    value={p[k] || ""}
                    onChange={(e) => upd(k, e.target.value)}
                  />
                </Field>
              ))}
            </div>
          </Section>

          <Section
            title="Description"
            action={
              <ReextractButton
                field="description"
                label="Description"
                busy={reextracting === "description"}
                onClick={() => reextractField("description", "Description")}
                canUndo={Boolean(p.field_history?.description !== undefined)}
                onUndo={() => undoField("description", "Description")}
              />
            }
          >
            <textarea
              data-testid="field-description"
              rows={4}
              className="w-full bg-transparent border-b border-[#E8E6DF] focus:border-[#B5953B] focus:outline-none py-2 text-sm resize-none"
              value={p.description || ""}
              onChange={(e) => upd("description", e.target.value)}
            />
          </Section>

          <Section title="Pricing">
            <p className="text-xs text-[#5C5C54] -mt-2 mb-3">
              Add one or more price points. Each is shown on the product page with its unit.
            </p>
            <div className="space-y-2">
              {(p.prices || []).map((pr, idx) => {
                const isCustom = !PRICE_UNIT_OPTIONS.find((o) => o.value === pr.unit && o.value !== "custom");
                return (
                  <div
                    key={idx}
                    data-testid={`price-row-${idx}`}
                    className="grid grid-cols-12 gap-2 items-center bg-[#FAFAF8] border border-[#E8E6DF] rounded-sm p-2.5"
                  >
                    <div className="col-span-5">
                      <select
                        value={isCustom ? "custom" : pr.unit || "piece"}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === "custom") {
                            setP((x) => ({
                              ...x,
                              prices: x.prices.map((q, i) =>
                                i === idx ? { ...q, unit: q.unit || "", _custom: true } : q,
                              ),
                            }));
                          } else {
                            setP((x) => ({
                              ...x,
                              prices: x.prices.map((q, i) =>
                                i === idx ? { ...q, unit: v, _custom: false } : q,
                              ),
                            }));
                          }
                        }}
                        className="w-full bg-white border border-[#E8E6DF] rounded-sm px-2 py-1.5 text-sm"
                      >
                        {PRICE_UNIT_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                      {(isCustom || pr._custom) && (
                        <input
                          placeholder="Custom unit (e.g. per metre)"
                          value={pr.unit && !PRICE_UNIT_OPTIONS.find((o) => o.value === pr.unit) ? pr.unit : ""}
                          onChange={(e) =>
                            setP((x) => ({
                              ...x,
                              prices: x.prices.map((q, i) =>
                                i === idx ? { ...q, unit: e.target.value } : q,
                              ),
                            }))
                          }
                          className="mt-2 w-full bg-white border border-[#E8E6DF] rounded-sm px-2 py-1.5 text-sm"
                        />
                      )}
                    </div>
                    <div className="col-span-3 flex items-center gap-1.5 bg-white border border-[#E8E6DF] rounded-sm px-2">
                      <span className="text-sm text-[#5C5C54]">₹</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={pr.value ?? ""}
                        onChange={(e) =>
                          setP((x) => ({
                            ...x,
                            prices: x.prices.map((q, i) =>
                              i === idx ? { ...q, value: e.target.value } : q,
                            ),
                          }))
                        }
                        className="flex-1 bg-transparent py-1.5 text-sm outline-none"
                      />
                    </div>
                    <input
                      placeholder="Note (optional)"
                      value={pr.notes || ""}
                      onChange={(e) =>
                        setP((x) => ({
                          ...x,
                          prices: x.prices.map((q, i) =>
                            i === idx ? { ...q, notes: e.target.value } : q,
                          ),
                        }))
                      }
                      className="col-span-3 bg-white border border-[#E8E6DF] rounded-sm px-2 py-1.5 text-sm"
                    />
                    <button
                      onClick={() =>
                        setP((x) => ({ ...x, prices: x.prices.filter((_, i) => i !== idx) }))
                      }
                      className="col-span-1 text-[#5C5C54] hover:text-[#9E2A2B] flex items-center justify-center"
                      data-testid={`price-remove-${idx}`}
                    >
                      <X size={14} strokeWidth={2} />
                    </button>
                  </div>
                );
              })}
              <button
                onClick={() =>
                  setP((x) => ({
                    ...x,
                    prices: [
                      ...(x.prices || []),
                      { unit: "piece", value: "", currency: "INR", notes: "" },
                    ],
                  }))
                }
                className="text-xs text-[#5E6033] hover:text-[#B5953B] inline-flex items-center gap-1 mt-2"
                data-testid="add-price-button"
              >
                <Plus size={12} strokeWidth={2} /> Add price
              </button>
            </div>
          </Section>

          <Section
            title="Features"
            action={
              <ReextractButton
                field="features"
                label="Features"
                busy={reextracting === "features"}
                onClick={() => reextractField("features", "Features")}
                canUndo={Boolean(p.field_history?.features !== undefined)}
                onUndo={() => undoField("features", "Features")}
              />
            }
          >
            <ListEditor
              items={p.features || []}
              onChange={(idx, v) => updList("features", idx, v)}
              onAdd={() => addList("features")}
              onRemove={(idx) => remList("features", idx)}
              testId="feature"
              placeholder="A short feature highlight"
            />
          </Section>

          <Section
            title="Specifications"
            action={
              <ReextractButton
                field="specifications"
                label="Specifications"
                busy={reextracting === "specifications"}
                onClick={() => reextractField("specifications", "Specifications")}
                canUndo={Boolean(p.field_history?.specifications !== undefined)}
                onUndo={() => undoField("specifications", "Specifications")}
              />
            }
          >
            <div className="space-y-2">
              {(p.specifications || []).map((s, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`spec-row-${idx}`}>
                  <input
                    placeholder="Key"
                    value={s.key || ""}
                    onChange={(e) => updSpec(idx, "key", e.target.value)}
                    className="col-span-4 bg-transparent border-b border-[#E8E6DF] focus:border-[#B5953B] focus:outline-none py-1.5 text-sm"
                  />
                  <input
                    placeholder="Value"
                    value={s.value || ""}
                    onChange={(e) => updSpec(idx, "value", e.target.value)}
                    className="col-span-7 bg-transparent border-b border-[#E8E6DF] focus:border-[#B5953B] focus:outline-none py-1.5 text-sm"
                  />
                  <button
                    onClick={() => remSpec(idx)}
                    className="col-span-1 text-[#5C5C54] hover:text-[#9E2A2B]"
                  >
                    <X size={14} strokeWidth={1.5} />
                  </button>
                </div>
              ))}
              <button
                onClick={addSpec}
                className="text-xs text-[#5E6033] hover:text-[#B5953B] inline-flex items-center gap-1 mt-2"
                data-testid="add-spec-button"
              >
                <Plus size={12} strokeWidth={1.5} /> Add specification
              </button>
            </div>
          </Section>

          <Section title="Profile / Variants">
            <div className="flex items-center justify-between -mt-2 mb-3 gap-3 flex-wrap">
              <p className="text-xs text-[#5C5C54] flex-1 min-w-[200px]">
                When the brochure lists several profile variants (e.g. GL 11, GL 12, ...), each row here
                becomes one row in the public Variants table. Leave fields blank if not applicable.
              </p>
              <button
                onClick={consolidateSpecs}
                className="text-[11px] border border-[#E8E6DF] hover:bg-[#F2F0EA] text-[#5E6033] px-2.5 py-1.5 rounded-sm inline-flex items-center gap-1 transition-colors"
                data-testid="consolidate-specs-button"
                title="Group 'GL 11 Length, GL 11 Weight, …' rows into a single Variants table"
              >
                <Sparkles size={11} strokeWidth={2} /> Consolidate from specs
              </button>
            </div>
            {(p.variants || []).length === 0 ? (
              <div className="text-xs text-[#5C5C54] italic mb-3">
                No variants — most products won&rsquo;t need this. Add one only if there are multiple
                profile codes/finishes that share the same product.
              </div>
            ) : (
              <div className="overflow-x-auto -mx-6 px-6">
                <table className="w-full min-w-[640px] text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-[#5C5C54]">
                      <th className="pb-2 pr-3 font-medium">Code</th>
                      <th className="pb-2 pr-3 font-medium">Finish</th>
                      <th className="pb-2 pr-3 font-medium">Length</th>
                      <th className="pb-2 pr-3 font-medium">Coverage</th>
                      <th className="pb-2 pr-3 font-medium">Weight</th>
                      <th className="pb-2 pr-3 font-medium">Thickness</th>
                      <th className="pb-2 w-6"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(p.variants || []).map((v, idx) => (
                      <tr key={idx} data-testid={`variant-row-${idx}`} className="border-t border-[#E8E6DF]">
                        {["code", "finish", "length", "coverage", "weight", "thickness"].map((field) => (
                          <td key={field} className="py-1.5 pr-2">
                            <input
                              value={v[field] || ""}
                              placeholder={field === "code" ? "GL 11" : "—"}
                              data-testid={`variant-${field}-${idx}`}
                              onChange={(e) =>
                                setP((x) => ({
                                  ...x,
                                  variants: x.variants.map((q, i) =>
                                    i === idx ? { ...q, [field]: e.target.value } : q,
                                  ),
                                }))
                              }
                              className="w-full bg-transparent border-b border-[#E8E6DF] focus:border-[#B5953B] focus:outline-none py-1 text-sm"
                            />
                          </td>
                        ))}
                        <td className="py-1.5">
                          <button
                            onClick={() =>
                              setP((x) => ({
                                ...x,
                                variants: x.variants.filter((_, i) => i !== idx),
                              }))
                            }
                            className="text-[#5C5C54] hover:text-[#9E2A2B]"
                            data-testid={`variant-remove-${idx}`}
                          >
                            <X size={14} strokeWidth={1.5} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <button
              onClick={() =>
                setP((x) => ({
                  ...x,
                  variants: [...(x.variants || []), { code: "" }],
                }))
              }
              className="text-xs text-[#5E6033] hover:text-[#B5953B] inline-flex items-center gap-1 mt-3"
              data-testid="add-variant-button"
            >
              <Plus size={12} strokeWidth={1.5} /> Add variant
            </button>
          </Section>

          {LISTS.map(([k, label]) => (
            <Section
              key={k}
              title={label}
              action={
                <ReextractButton
                  field={k}
                  label={label}
                  busy={reextracting === k}
                  onClick={() => reextractField(k, label)}
                  canUndo={Boolean(p.field_history?.[k] !== undefined)}
                  onUndo={() => undoField(k, label)}
                />
              }
            >
              <ListEditor
                items={p[k] || []}
                onChange={(idx, v) => updList(k, idx, v)}
                onAdd={() => addList(k)}
                onRemove={(idx) => remList(k, idx)}
                testId={k}
                placeholder={`Add ${label.toLowerCase()}`}
              />
            </Section>
          ))}

          <Section
            title="Technical information"
            action={
              <ReextractButton
                field="technical_info"
                label="Technical information"
                busy={reextracting === "technical_info"}
                onClick={() => reextractField("technical_info", "Technical information")}
                canUndo={Boolean(p.field_history?.technical_info !== undefined)}
                onUndo={() => undoField("technical_info", "Technical information")}
              />
            }
          >
            <textarea
              rows={3}
              data-testid="field-technical-info"
              className="w-full bg-transparent border-b border-[#E8E6DF] focus:border-[#B5953B] focus:outline-none py-2 text-sm resize-none"
              value={p.technical_info || ""}
              onChange={(e) => upd("technical_info", e.target.value)}
            />
          </Section>
          <Section
            title="Installation instructions"
            action={
              <ReextractButton
                field="installation_instructions"
                label="Installation instructions"
                busy={reextracting === "installation_instructions"}
                onClick={() => reextractField("installation_instructions", "Installation instructions")}
                canUndo={Boolean(p.field_history?.installation_instructions !== undefined)}
                onUndo={() => undoField("installation_instructions", "Installation instructions")}
              />
            }
          >
            <textarea
              rows={3}
              data-testid="field-installation"
              className="w-full bg-transparent border-b border-[#E8E6DF] focus:border-[#B5953B] focus:outline-none py-2 text-sm resize-none"
              value={p.installation_instructions || ""}
              onChange={(e) => upd("installation_instructions", e.target.value)}
            />
          </Section>

          <Section title="Web research (Perplexity)">
            {p.research_notes ? (
              <div data-testid="admin-research-block">
                <p className="text-xs text-[#5C5C54] -mt-2 mb-3">
                  Researched on the web — also shown on the public product page in &ldquo;Beyond the brochure&rdquo;
                  and fed to the AI chatbot for richer answers.
                </p>
                <div className="text-sm text-[#1A1A18]/90 leading-relaxed whitespace-pre-line bg-[#FAFAF8] border border-[#E8E6DF] rounded-sm p-3 max-h-72 overflow-y-auto">
                  {p.research_notes}
                </div>
                {p.research_sources?.length > 0 && (
                  <div className="mt-3">
                    <div className="text-[10px] uppercase tracking-wider text-[#5C5C54] mb-1.5">
                      Sources ({p.research_sources.length})
                    </div>
                    <ul className="space-y-1">
                      {p.research_sources.slice(0, 6).map((u, i) => (
                        <li key={i} className="text-xs break-all">
                          <a
                            href={u}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[#5E6033] hover:text-[#B5953B] underline"
                          >
                            {u}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : p.research_error ? (
              <div
                className="bg-[#9E2A2B]/10 border border-[#9E2A2B]/30 text-[#9E2A2B] text-xs rounded-sm p-3"
                data-testid="research-error-banner"
              >
                <div className="font-semibold mb-1">Perplexity research failed</div>
                <div className="font-mono text-[11px] leading-relaxed break-words">
                  {p.research_error}
                </div>
                <div className="text-[11px] mt-2 opacity-80">
                  Most common cause: an invalid <code>PERPLEXITY_MODEL</code> env var. Set it to <code>sonar</code>
                  (or remove it entirely) in Manage Deployments → Secrets, then redeploy and click <strong>Re-run AI</strong>.
                </div>
              </div>
            ) : (
              <div className="text-xs text-[#5C5C54] italic">
                {p.extraction_status === "completed"
                  ? "Perplexity didn't return notes for this product. Check that PERPLEXITY_API_KEY is set in the deployed environment and click Re-run AI."
                  : "Will appear here once AI extraction + web research complete."}
              </div>
            )}
          </Section>
        </div>

        {/* Right: Images + AI Suggestions */}
        <div className="space-y-6">
          <Section title="Product images">
            <SortableImageGrid
              productId={id}
              images={p.images || []}
              onImagesChange={(imgs) => setP((x) => ({ ...x, images: imgs }))}
              onAddFiles={uploadMoreImages}
            />

            <button
              onClick={generateScenes}
              disabled={generatingScenes || p.scene_status === "generating"}
              data-testid="generate-scenes-button"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#1A1A18] hover:bg-[#2a2a26] disabled:opacity-60 text-white px-4 py-3 rounded-sm text-sm font-semibold transition-colors"
            >
              {generatingScenes || p.scene_status === "generating" ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Rendering scenes in background…
                </>
              ) : (
                <>
                  <Wand2 size={14} strokeWidth={2} />
                  Generate 4 AI scene renders
                </>
              )}
            </button>
            {p.scene_status === "generating" && (
              <div
                className="mt-3 bg-[#FDF7E3] border border-[#B5953B]/40 text-[#5E6033] text-xs rounded-sm p-3"
                data-testid="scene-generating-banner"
              >
                <div className="font-semibold mb-0.5">AI scene rendering in progress…</div>
                <div className="leading-relaxed opacity-90">
                  Nano Banana takes ~15s per image. The new renders will appear automatically
                  once ready — you can leave this page and come back.
                </div>
              </div>
            )}
            {p.scene_status === "failed" && p.scene_error && (
              <div
                className="mt-3 bg-[#9E2A2B]/10 border border-[#9E2A2B]/30 text-[#9E2A2B] text-xs rounded-sm p-3"
                data-testid="scene-error-banner"
              >
                <div className="font-semibold mb-1">Scene generation failed</div>
                <div className="font-mono text-[11px] leading-relaxed break-words">
                  {p.scene_error}
                </div>
                <div className="text-[11px] mt-2 opacity-80">
                  Click <strong>Generate 4 AI scene renders</strong> to try again.
                </div>
              </div>
            )}
            <p className="text-[11px] text-[#5C5C54] mt-2 leading-relaxed">
              Uses Gemini Nano Banana to render the product applied in living room, wardrobe, kitchen back-panel and door scenes — photorealistic.
            </p>
          </Section>

          <Section title="Source PDFs">
            <p className="text-xs text-[#5C5C54] -mt-2 mb-3">
              The original brochures / catalogs Hedaply AI reads. You can add more PDFs
              anytime — the QR code stays the same. After attaching, click <strong>Re-run AI</strong>
              to merge new information into this product.
            </p>
            <SourceFilesEditor
              productId={id}
              files={p.files || []}
              onChange={(updated) =>
                setP((x) => ({ ...x, files: updated.files, images: updated.images }))
              }
            />
          </Section>

          <Section title="Reference Sheets">
            <p className="text-xs text-[#5C5C54] -mt-2 mb-3">
              Snapshot pages from your PDF (color charts, specs, applications, installation).
              Toggle which ones to show on the public product page.
            </p>
            {(p.reference_sheets || []).length === 0 ? (
              <div className="text-xs text-[#5C5C54] italic">
                None yet — run AI extraction on a PDF and Hedaply will auto-snapshot the relevant pages.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {(p.reference_sheets || []).map((s, idx) => (
                  <div
                    key={s.id}
                    className="border border-[#E8E6DF] rounded-sm overflow-hidden bg-white"
                    data-testid={`ref-sheet-${idx}`}
                  >
                    <div className="aspect-[3/4] bg-[#F2F0EA] overflow-hidden">
                      <img
                        src={fileUrl(s.storage_path)}
                        alt={s.title || s.label}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-2.5">
                      <div className="hed-overline" style={{ fontSize: 9 }}>
                        {s.label.replace("_", " ")}
                      </div>
                      <div className="text-xs font-semibold mt-0.5 truncate">{s.title}</div>
                      <div className="flex items-center justify-between mt-2 gap-2">
                        <label className="flex items-center gap-1.5 text-[11px] cursor-pointer">
                          <input
                            type="checkbox"
                            checked={s.include_public}
                            onChange={async (e) => {
                              const checked = e.target.checked;
                              setP((x) => ({
                                ...x,
                                reference_sheets: x.reference_sheets.map((q) =>
                                  q.id === s.id ? { ...q, include_public: checked } : q,
                                ),
                              }));
                              try {
                                await api.patch(`/products/${id}/sheets/${s.id}`, {
                                  include_public: checked,
                                });
                              } catch (err) {
                                /* ignore */
                              }
                            }}
                            data-testid={`ref-sheet-toggle-${idx}`}
                          />
                          Show
                        </label>
                        <button
                          onClick={async () => {
                            if (!window.confirm("Remove this reference sheet?")) return;
                            try {
                              await api.delete(`/products/${id}/sheets/${s.id}`);
                              setP((x) => ({
                                ...x,
                                reference_sheets: x.reference_sheets.filter((q) => q.id !== s.id),
                              }));
                            } catch (err) {
                              alert("Failed to remove");
                            }
                          }}
                          className="text-[#5C5C54] hover:text-[#9E2A2B]"
                          data-testid={`ref-sheet-remove-${idx}`}
                        >
                          <X size={12} strokeWidth={2} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>

          <div className="hed-card p-6 bg-[#F2F0EA] border-[#E8E6DF]">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles size={14} strokeWidth={1.5} className="text-[#B5953B]" />
              <div className="hed-overline">AI Suggested Information</div>
            </div>
            <p className="text-xs text-[#5C5C54] mb-4">
              Approve to include on the product page & in the AI assistant. Nothing is saved permanently until you approve.
            </p>

            <SuggestionGroup
              title="Recommended applications"
              items={p.suggestions?.recommended_applications || []}
              isApproved={(v) => isApproved("recommended_applications", v)}
              toggle={(v) => toggleApprove("recommended_applications", v)}
              testIdPrefix="sugg-rec-app"
            />
            <SuggestionGroup
              title="Maintenance instructions"
              items={p.suggestions?.maintenance_instructions || []}
              isApproved={(v) => isApproved("maintenance_instructions", v)}
              toggle={(v) => toggleApprove("maintenance_instructions", v)}
              testIdPrefix="sugg-maint"
            />
            <SuggestionGroup
              title="Use cases"
              items={p.suggestions?.use_cases || []}
              isApproved={(v) => isApproved("use_cases", v)}
              toggle={(v) => toggleApprove("use_cases", v)}
              testIdPrefix="sugg-use"
            />
            <SuggestionGroup
              title="Installation recommendations"
              items={p.suggestions?.installation_recommendations || []}
              isApproved={(v) => isApproved("installation_recommendations", v)}
              toggle={(v) => toggleApprove("installation_recommendations", v)}
              testIdPrefix="sugg-install"
            />
            <SuggestionFaqGroup
              items={p.suggestions?.faqs || []}
              isApproved={(v) => isApproved("faqs", v)}
              toggle={(v) => toggleApprove("faqs", v)}
            />
          </div>
        </div>
      </div>

      {qrOpen && <QrModal product={p} onClose={() => setQrOpen(false)} />}
    </AdminLayout>
  );
}

